import {Symbol, SymbolStates} from "./Symbol";
import {gsap} from "gsap";
import {Game} from "../Game";
import {MonsterSymbol} from "./MonsterSymbol";
import {Sound} from "../sound/Sound";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {IMonster, MONSTER_EXPRESSION, MONSTER_WEAPON_ACTIONS} from "../server/IData";
import {GameModeNames} from "../server/GameModeNames";

export class ReelModel {
    private _reelPosAvailableForFillUp: number[] = [];
    private _mxStepInCollapse: number = 0;
    private _id: number;
    private _numSymbolsRemovedForAvalanche: number;
    private _symbols: Symbol[];

    constructor(id: number) {
        this._id = id;

        this._symbols = [];
        this._numSymbolsRemovedForAvalanche = 0;
        this._reelPosAvailableForFillUp = [];
    }

    public init(symbols: string[], symbolContainer: PIXI.Container, monsterSymbolContainer: PIXI.Container): void {
        for (let rowId: number = 0; rowId < Game.ACTIVE_ROWS_NUM; rowId++) {
            let symbol: Symbol | MonsterSymbol;
            if (Game.MONSTER_SYMBOLS.includes(symbols[rowId])) {
                symbol = new MonsterSymbol(symbols[rowId], SymbolStates.NORMAL, rowId, 5, true);
                this.handleMonsterWound(symbol as MonsterSymbol);

                monsterSymbolContainer.addChild(symbol);
            } else {
                if (symbols[rowId] === "AV") {
                    symbol = new Symbol("P4", SymbolStates.NORMAL, rowId);
                    symbol.isRemoved = true;
                    this._numSymbolsRemovedForAvalanche++;

                    this._reelPosAvailableForFillUp.push(rowId);
                } else {
                    symbol = new Symbol(symbols[rowId], SymbolStates.NORMAL, rowId);
                }

                if (!["CR", "AV"].includes(symbols[rowId])) {
                    symbolContainer.addChild(symbol);
                }
            }
            symbol.position.set((this._id * Game.SYMBOL_WIDTH), (rowId * Game.SYMBOL_HEIGHT));
            symbol.scale.set(Game.scaleFactor);

            if (symbols[rowId] !== "AV") {
                this._symbols.push(symbol);
            }
        }
    }

    public allOut(): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        this._numSymbolsRemovedForAvalanche = 0;
        this._reelPosAvailableForFillUp = [];

        this._symbols.forEach((sym: Symbol, idx: number) => {
            tl.to(sym, {
                duration: 0.3,
                y: sym.y + (10 * Game.SYMBOL_HEIGHT),
                ease: "sine.in"
            }, 0.08 * (this._symbols.length - 1 - idx));
        });

        tl.add(() => {
            if (this._id === Game.ACTIVE_REELS_NUM - 1) {
                Game.reelsView.symbolContainer.removeChildren();
                Game.monsterReelsView.monsterSymbolContainer.removeChildren();
            }

            this._symbols = [];
            Game.noOfScatterLanded = 0;
            Game.noOfWildLanded = 0;
        });

        return tl;
    }

    public allIn(symbols: string[]): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            symTranslateDuration: number = 0.2,
            skinLevelAsPerMultiplier: string = Game.multiplier.getSkinLevel();

        this._numSymbolsRemovedForAvalanche = 0;
        this._reelPosAvailableForFillUp = [];

        const reelLandSound: string = Sound.REELS_LANDING[MathHelper.randomIntInRange(0, 8)];

        for (let rowId: number = 0; rowId < Game.ACTIVE_ROWS_NUM; rowId++) {
            let symbol: Symbol | MonsterSymbol;
            if (Game.MONSTER_SYMBOLS.includes(symbols[rowId])) {
                symbol = new MonsterSymbol(symbols[rowId], SymbolStates.NORMAL, rowId, 5, true);
                (symbol as MonsterSymbol).grExecuteCount = 0;

                this.handleMonsterWound(symbol as MonsterSymbol);

                Game.monsterReelsView.monsterSymbolContainer.addChild(symbol);
            } else {
                symbol = new Symbol(symbols[rowId], SymbolStates.NORMAL, rowId);
                if (symbol && symbols[rowId] !== "CR") {
                    Game.reelsView.symbolContainer.addChild(symbol);
                }
            }
            symbol.position.set((Game.SYMBOL_WIDTH * this._id), ((rowId - (Game.ACTIVE_ROWS_NUM + 1)) * (Game.SYMBOL_HEIGHT)));
            symbol.scale.set(Game.scaleFactor);

            if (skinLevelAsPerMultiplier) {
                this.updateSymbolSkin(skinLevelAsPerMultiplier, symbol);
            }

            this._symbols.push(symbol);
        }

        // keep MX sym always on top
        if (Game.mxSymbol && Game.mxSymbol.parent) {
            Game.mxSymbol.parent.addChild(Game.mxSymbol);
        }

        this._symbols.forEach((sym: Symbol, rowId: number) => {
            const symTl: gsap.core.Timeline = gsap.timeline({
                    delay: 0.05 * (this._symbols.length - 1 - rowId)
                }),
                toY: number = (sym.reelPosition * Game.SYMBOL_HEIGHT);

            symTl.to(sym, {
                duration: symTranslateDuration,
                y: toY,
                ease: "power1.in"
            });

            if (rowId === 0) {
                tl.add(() => {
                    Game.soundPlayer.playSound(reelLandSound, 1);
                }, symTranslateDuration + ((this._id === 0 || this._id === Game.ACTIVE_REELS_NUM - 1) ? 0.05 : 0));
            }

            if (Game.BOOSTER_SYMBOLS.includes(sym.name)) {
                const boosterSoundKey: string = sym.name + "_LAND";
                symTl.add(() => {
                    Game.soundPlayer.playSound((Sound as any)[boosterSoundKey], 1);
                }, symTranslateDuration);
            }

            symTl.add(this.symbolBounce(sym, toY, true), symTranslateDuration);

            tl.add(symTl, 0);
        });

        return tl;
    }

    public collapse(reelDelay: number): gsap.core.Timeline {
        this._symbols = this._symbols.filter((val: Symbol) => {
            return (!val.isRemoved);
        });

        this._symbols.sort((a: Symbol, b: Symbol) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });

        this._numSymbolsRemovedForAvalanche = 0;
        this._reelPosAvailableForFillUp = [];

        const reelsBeforeAvalanche: string[] = Game.model.gameData.reelsBeforeAvalanche[this._id],
            reelsBeforeAvalancheModified: string[] = JSON.parse(JSON.stringify(reelsBeforeAvalanche)),
            reelAfterAvalanche: string[] = Game.model.gameData.reelsAfterAvalanche[this._id];

        let mxCountInReel: number = 0,
            isPlayReelCollapse: boolean = true;

        for (let i: number = reelAfterAvalanche.length - 1; i >= 0; i--) {
            if (reelAfterAvalanche[i] === "AV") {
                this._numSymbolsRemovedForAvalanche++;

                this._reelPosAvailableForFillUp.push(i);
            }

            if (reelsBeforeAvalanche[i] === "MX") {
                mxCountInReel++;
            }
        }

        for (let reelPos: number = reelsBeforeAvalancheModified.length - 1; reelPos >= 0; reelPos--) {
            const symName: string = reelsBeforeAvalancheModified[reelPos];

            if (symName !== "CR" && symName !== "L" && symName !== "AV") {
                let avCountAfterSelfInModified: number = 0,
                    shouldIncludeMXHeight: boolean = false;
                const mxIdx: number = reelsBeforeAvalancheModified.lastIndexOf("MX");

                if (symName === "MX" && this._mxStepInCollapse > 0) {
                    reelsBeforeAvalancheModified[reelPos + this._mxStepInCollapse] = symName;
                    reelsBeforeAvalancheModified[reelPos] = "AV";

                    continue;
                }

                if (symName === "MX") {
                    continue;
                }

                for (let i: number = reelPos + 1; i < reelsBeforeAvalancheModified.length; i++) {
                    if (reelsBeforeAvalancheModified[i] === "AV") {
                        if (i > mxIdx) {
                            shouldIncludeMXHeight = true;
                        }
                        avCountAfterSelfInModified++;
                    }
                }

                if (mxIdx > reelPos && shouldIncludeMXHeight) {
                    avCountAfterSelfInModified += mxCountInReel;
                }

                if (avCountAfterSelfInModified > 0) {
                    reelsBeforeAvalancheModified[reelPos + avCountAfterSelfInModified] = symName;
                    reelsBeforeAvalancheModified[reelPos] = "AV";
                }

                const symbol: Symbol = this.getSymbolAt(reelPos, reelsBeforeAvalanche[reelPos]);
                if (symbol && reelsBeforeAvalanche[reelPos] !== "MX" && symbol.name !== "CR" && !symbol.name.includes("MX")) {
                    symbol.reelPosition = (reelPos + avCountAfterSelfInModified);
                }
            }
        }

        if (this._id === Game.mxVSymbolReelId && Game.mxSymbol) {
            Game.mxSymbol.reelPosition = Game.mxVSymbolRowIdAfterCollapse;
        }

        this._symbols.sort((a: Symbol, b: Symbol) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });

        const tl: gsap.core.Timeline = gsap.timeline({
            delay: reelDelay
        });
        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol: Symbol = this._symbols[i];
            if (!symbol) {
                throw new Error(`collapse method in ReelModel, symbol is null!`);
            } else {
                const toY: number = (Game.SYMBOL_HEIGHT * symbol.reelPosition);

                if (symbol.y !== toY) {
                    tl.to(symbol, {
                        duration: 0.2,
                        y: toY,
                        ease: "power2.in"
                    }, 0);

                    if (Game.MONSTER_SYMBOLS.includes(symbol.name)) {
                        const soundKey: string = "tankLand" + MathHelper.randomIntInRange(1, 4);
                        tl.add([
                            symbol.getStateAnimation(SymbolStates.LAND, 5),
                            () => {
                                Game.soundPlayer.playSound(soundKey, 1);
                            }
                        ], 0.2);

                        tl.add(()=>{
                            (symbol as MonsterSymbol).getStateAnimation(SymbolStates.NORMAL, 5, true);
                        });
                    }

                    if (isPlayReelCollapse) {
                        tl.add(() => {
                            Game.soundPlayer.playSound(Sound.REELS_COLLAPSE[MathHelper.randomIntInRange(0, 8)], 1);
                        }, 0.2);
                        isPlayReelCollapse = false;
                    }
                }


            }
        }

        return tl;
    }

    public fillUp(symbols: string[]): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            symTranslateDuration: number = 0.2,
            skinLevelAsPerMultiplier: string = Game.multiplier.getSkinLevel(),
            reelLandSound: string = Sound.REELS_LANDING[MathHelper.randomIntInRange(0, 8)];
        let fillUpCount: number = 0;
        let isFirst: boolean = false;
        for (let rowId: number = 0; rowId < Game.ACTIVE_ROWS_NUM; rowId++) {
            let symbol: Symbol | MonsterSymbol;

            if (this._reelPosAvailableForFillUp.includes(rowId)) {
                if (Game.MONSTER_SYMBOLS.includes(symbols[rowId])) {
                    symbol = new MonsterSymbol(symbols[rowId], SymbolStates.NORMAL, rowId, 5, true);

                    this.handleMonsterWound(symbol as MonsterSymbol);

                    Game.monsterReelsView.monsterSymbolContainer.addChild(symbol);

                    // any monster on bottom, push it above this
                    if (rowId + 1 < Game.ACTIVE_ROWS_NUM) {
                        const botReelsSymName: string = Game.model.gameData.evaluatedArea[this._id][rowId + 1];

                        if (Game.MONSTER_SYMBOLS.includes(botReelsSymName)) {
                            const botMonster: Symbol = this.getSymbolAt(rowId + 1, botReelsSymName);

                            if (botMonster && botMonster.parent) {
                                botMonster.parent.addChild(botMonster);
                            }
                        }
                    }

                    // any monster on right, push it above this
                    if (this._id + 1 < Game.ACTIVE_REELS_NUM) {
                        const rightReelsSymName: string = Game.model.gameData.evaluatedArea[this._id + 1][rowId];

                        if (Game.MONSTER_SYMBOLS.includes(rightReelsSymName)) {
                            const rightMonster: Symbol = Game.reelsView.reelModels[this._id + 1].getSymbolAt(rowId, rightReelsSymName);

                            if (rightMonster && rightMonster.parent) {
                                rightMonster.parent.addChild(rightMonster);
                            }
                        }
                    }
                } else {
                    symbol = new Symbol(symbols[rowId], SymbolStates.NORMAL, rowId);
                    if (symbols[rowId] !== "CR") {
                        Game.reelsView.symbolContainer.addChild(symbol);
                    }
                }
                symbol.position.set((Game.SYMBOL_WIDTH * this._id), (-800 - (fillUpCount++ * Game.SYMBOL_HEIGHT)));
                symbol.scale.set(Game.scaleFactor);

                if (skinLevelAsPerMultiplier) {
                    this.updateSymbolSkin(skinLevelAsPerMultiplier, symbol);
                }

                this._symbols.push(symbol);
            }
        }

        this._symbols.sort((a: Symbol, b: Symbol) => {
            return (a.reelPosition < b.reelPosition) ? -1 : 1;
        });

        // keep MX sym always on top
        if (Game.mxSymbol && Game.mxSymbol.parent) {
            Game.mxSymbol.parent.addChild(Game.mxSymbol);
        }

        for (let i: number = 0; i < this._symbols.length; i++) {
            const symbol: Symbol = this._symbols[i];
            if (!symbol) {
                throw new Error(`fillUp method in ReelModel, symbol is null!`);
            } else {
                const toY: number = (Game.SYMBOL_HEIGHT * symbol.reelPosition);
                if (symbol.y !== toY) {
                    const symTl: gsap.core.Timeline = gsap.timeline({
                        delay: 0.05 * (this._symbols.length - 1 - i)
                    });
                    symTl.to(symbol, {
                        duration: symTranslateDuration,
                        y: toY,
                        ease: "power1.in",
                        onComplete: () => {
                            if (!isFirst) {
                                Game.soundPlayer.playSound(reelLandSound, 1);
                                isFirst = true
                            }
                        }
                    });


                    // if (Game.BOOSTER_SYMBOLS.includes(symbol.name) && Game.isSymOnDiffRow(Game.model.gameData.reels, symbol.name, this._id, i)) {
                    if (Game.BOOSTER_SYMBOLS.includes(symbol.name)) {
                        const boosterSoundKey: string = symbol.name + "_LAND";
                        symTl.add(() => {
                            Game.soundPlayer.playSound((Sound as any)[boosterSoundKey], 1);
                        }, symTranslateDuration);

                    }

                    let soundDurIdx: number = symbol.name === "C4" ? 0.1 : symTranslateDuration;

                    if (Game.MONSTER_SYMBOLS.includes(symbol.name)) {
                        symTl.add(() => {
                            if (symbol.name === "C4") {
                                tl.add(() => {
                                    Game.soundPlayer.playSound(symbol.name + "Response" + MathHelper.randomIntInRange(1, 7), 1);
                                }, 0.4);
                            } else {
                                tl.add(() => {
                                    Game.soundPlayer.playSound(symbol.name + "Response" + MathHelper.randomIntInRange(1, 4), 1);
                                }, 0.4)
                            }
                        }, soundDurIdx)
                    }

                    Game.prepMonstersToDieAfterAvalanche();

                    symTl.add(this.symbolBounce(symbol, toY, false), symTranslateDuration);

                    tl.add(symTl, 0);
                }
            }
        }

        this._numSymbolsRemovedForAvalanche = 0;
        this._reelPosAvailableForFillUp = [];

        return tl;
    }

    public removeSymbol(sym: Symbol): gsap.core.Tween {
        // #465
        if (sym.name.includes("MX")) {
            return gsap.to(this, {});
        }

        return gsap.to(sym.scale, {
            duration: 0.5,
            x: 0,
            y: 0,
            onComplete: () => {
                if (sym.parent) {
                    sym.parent.removeChild(sym);
                }
            }
        });
    }

    public getSymbolAt(reelPosition: number, symName?: string): Symbol {
        let anotherSymbolIdx: number = -1;

        for (let i: number = 0; i < this._symbols.length; i++) {
            symName = symName || this._symbols[i].name;

            if (this._symbols[i].reelPosition === reelPosition) {
                if ((symName === this._symbols[i].name) || (symName === this._symbols[i].aliasName)) { // #248
                    const retVal: Symbol = this._symbols[i];
                    if (anotherSymbolIdx !== -1) {
                        const item: Symbol = this._symbols.splice(anotherSymbolIdx, 1)[0];
                        if (item.parent && !item.name.includes("MX")) {
                            item.parent.removeChild(item);
                        }
                    }
                    return retVal;
                } else if (!this._symbols[i].name.includes("MX")) { // #462
                    anotherSymbolIdx = i;
                }
            }
        }

        return this._symbols[reelPosition];
    }

    public getMonsterSymbolAt(monIdx: number): MonsterSymbol {
        for (let i: number = 0; i < Game.monsterReelsView.monsterSymbolContainer.children.length; i++) {
            const mon: PIXI.DisplayObject = Game.monsterReelsView.monsterSymbolContainer.children[i];
            if (mon.name === Game.MONSTER_SYMBOLS[(monIdx - Game.MONSTER_SYMBOLS.length)]) {
                return (mon as MonsterSymbol);
            }
        }

        return (this._symbols[0] as MonsterSymbol);
    }

    public getMonsterSymbolAtRowId(rowId: number): MonsterSymbol {
        return (this._symbols[rowId] as MonsterSymbol);
    }

    public increaseRows(startIdxForRows: number, extraRows: number, addRowsInBottom: boolean, tl: gsap.core.Timeline, duration: number): void {
        if (addRowsInBottom) {
            for (let row: number = startIdxForRows; row < extraRows + Game.ACTIVE_ROWS_NUM; row++) {
                const tempSymbol: Symbol = new Symbol("P4", SymbolStates.NORMAL, row);
                tempSymbol.position.set((this._id * Game.SYMBOL_WIDTH), (row * Game.SYMBOL_HEIGHT));
                tempSymbol.isRemoved = true;

                this._symbols.push(tempSymbol);
            }
        } else {
            if (extraRows) {
                for (let i: number = 0; i < this._symbols.length; i++) {
                    this._symbols[i].reelPosition += extraRows;
                }

                for (let row: number = 0; row < extraRows; row++) {
                    const tempSymbol: Symbol = new Symbol("P4", SymbolStates.NORMAL, row);
                    tempSymbol.position.set((this._id * Game.SYMBOL_WIDTH), (row * Game.SYMBOL_HEIGHT));
                    tempSymbol.isRemoved = true;

                    this._symbols.unshift(tempSymbol);
                }
            }
        }

        for (let i: 0 = 0; i < this._symbols.length; i++) {
            const sym: Symbol = this._symbols[i];

            tl.to(sym.scale, {
                duration: duration,
                x: Game.scaleFactor,
                y: Game.scaleFactor
            }, "0");
            tl.to(sym, {
                duration: duration,
                x: (Game.SYMBOL_WIDTH * this._id),
                y: (Game.SYMBOL_HEIGHT * sym.reelPosition)
            }, "<");
        }
    }

    private updateSymbolSkin(skinLevelAsPerMultiplier: string, symbol: Symbol): void {
        if (Game.ORDINARY_PAYOUT_SYMBOLS.includes(symbol.name)) {
            symbol.changeSymbolSkin(symbol.name + skinLevelAsPerMultiplier);
        } else if (symbol.name === "W") {
            symbol.changeSymbolSkin(((symbol.wildSkinName.split("_"))[0]) + skinLevelAsPerMultiplier);
        } else if (!symbol.name.includes("MX") && symbol.name.charAt(0) == "M") {
            symbol.changeSymbolSkin(symbol.getMultiplierSkinLevel());
        }
    }

    private symbolBounce(sym: Symbol, toY: number, calledFromAllIn: boolean): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline(),
            bounceDuration: number = 0.02 + sym.reelPosition * 0.005;

        if (Game.BOOSTER_SYMBOLS.includes(sym.name)) {
            tl.add(Game.overlaysForSymbols.playLandForTankBoosters(sym, toY));
        } else if (sym.name === "XS" || sym.name === "W" || sym.name === "S" || sym.name === "XB" || Game.MONSTER_SYMBOLS.includes(sym.name) || sym.name === "GE" || sym.name === "XN") {
            tl.add(sym.getStateAnimation(SymbolStates.LAND, 5));
            let soundPlayPos: string = "<";

            if (sym.name === "GE") {
                soundPlayPos = "0";
                tl.add(() => {
                    sym.getStateAnimationWithoutTL(SymbolStates.NORMAL, 5, true);
                }, sym.animation.spineData.findAnimation(SymbolStates.LAND).duration);
            }

            // TODO: need to refactor
            if (sym.name === "S") {
                tl.add(() => {
                    Game.noOfScatterLanded++;
                    const scatterSoundKey: string = "scatterLand" + Game.noOfScatterLanded;
                    Game.soundPlayer.playSound(scatterSoundKey, 1);
                }, "<");
                tl.add(() => {
                    Game.scatterInventory.playScatterAttention(sym);
                });
                // } else if (sym.name === "W" && Game.isSymOnDiffRow(Game.model.gameData.reels, sym.name, this._id, sym.reelPosition)) {
            } else if (sym.name === "W") {
                tl.add(() => {
                    Game.noOfWildLanded++;
                    const wildSoundKey: string = "wildLand" + (Game.noOfWildLanded % 5);
                    Game.soundPlayer.playSound(wildSoundKey, 1);
                }, "<");
            } else if (["XS", "XB", "GE", "XN"].includes(sym.name)) {
                const soundKey: string = sym.name + "_LAND";
                tl.add(() => {
                    Game.soundPlayer.playSound((Sound as any)[soundKey], 1);
                }, soundPlayPos);
            } else if (Game.MONSTER_SYMBOLS.includes(sym.name)) {
                const soundKey: string = "tankLand" + MathHelper.randomIntInRange(1, 4);
                tl.add(() => {
                    Game.soundPlayer.playSound(soundKey, 1);
                }, "<");

                tl.add(() => {
                    sym.getStateAnimationWithoutTL(SymbolStates.NORMAL, 0, true);
                });

                if (!calledFromAllIn && Game.visibleMonstersCount !== Game.MONSTER_SYMBOLS.length) {
                    tl.add([
                        (sym as MonsterSymbol).getStateAnimation(MONSTER_EXPRESSION.LAUGH, 5),
                        () => {
                            Game.soundPlayer.playSound(sym.name + "Laugh" + MathHelper.randomIntInRange(1, 3), 1);
                        }
                    ]);
                } else if (((Game.model.gameData.mode === GameModeNames.NORMAL_AVALANCHE) || (Game.model.gameData.mode.includes("FREESPIN") && !Game.model.gameData.mayhemSpin)) && Game.MONSTERS_HURT_STATUS[Game.MONSTER_NAME_TO_IDX[sym.name]]) {
                    tl.add((sym as MonsterSymbol).getStateAnimation(MONSTER_EXPRESSION.ANGRY, 5));
                }

                const monsters: IMonster[] = Game.model.data.monsters;
                let hasRecharged: boolean = false;

                if (Game.model.data.mode.includes("AVALANCHE")){
                    monsters.some((monster: IMonster) => { // using some for early termination of loop
                        const monsterSymName: string = "C" + (monster.symbol - Game.MONSTER_SYMBOLS.length + 1);
                        const monsterLife: number = monster.path && monster.path.length > 0 ? (Game.monstersToDieAfterAvalanche.includes(monsterSymName) ? monster.lifeLeft + 1: monster.lifeLeft): 0 ;
                        if ((monster.symbol-Game.MONSTER_SYMBOLS.length) === (Game.MONSTER_NAME_TO_IDX[sym.name]) && (Game.reelCorners.getLife(Game.MONSTER_NAME_TO_IDX[sym.name]) < monsterLife)){
                            tl.add([
                                (sym as MonsterSymbol).getStateAnimation("health_up", 0),
                                Game.reelCorners.playHealthUpOnReelCorner(Game.MONSTER_NAME_TO_IDX[sym.name]),
                                () => {
                                    Game.reelCorners.resetLifeOfACorner(Game.MONSTER_NAME_TO_IDX[sym.name], monsterLife);
                                }
                            ]);

                            tl.add(()=>{
                                (sym as MonsterSymbol).getStateAnimation(SymbolStates.NORMAL, 5, true);
                            });

                            if (Game.isPlayHeartRestore){
                                tl.add(()=>{
                                    Game.soundPlayer.playSound(Sound.HEART_RESTORE);
                                }, 0)
                                Game.isPlayHeartRestore = false;
                            }
                            hasRecharged = true;
                        }

                        return hasRecharged;
                    });
                }

                tl.add(()=>{
                    (sym as MonsterSymbol).getStateAnimation(SymbolStates.NORMAL, 5, true);
                });
            }
        } else if (!sym.name.includes("MX") && sym.name.charAt(0) === 'M') {
            let soundKey: string = "crate" + MathHelper.randomIntInRange(1, 4);
            if (["M10", "M20", "M50", "M100", "M200", "M500", "M1000"].includes(sym.name)) {
                soundKey = this.getMultiplierLandSoundKey(sym.name);
            }
            tl.add(Game.overlaysForSymbols.playMultiplierLand(sym, toY), "<");
            tl.add(() => {
                Game.soundPlayer.playSound(soundKey, 1);
            }, "<");
        } else if (calledFromAllIn && Game.ORDINARY_PAYOUT_SYMBOLS.includes(sym.name)) {
            tl.to(sym, {
                y: toY - 5,
                duration: bounceDuration,
                ease: "power3.in"
            });
            tl.to(sym, {
                y: toY,
                duration: 0.03 + sym.reelPosition * 0.005,
                ease: "power3.out"
            });
        } else if (sym.name.indexOf("MX") > -1) {
            tl.add(sym.playMXAnimations());
        }

        return tl;
    }

    private handleMonsterWound(monsterSymbol: MonsterSymbol): void {
        let woundSkin: string = "base";
        let lifeLeft: number = Game.model.data.lifeLeftInitial;

        if (Game.model.data.mode.includes("AVALANCHE")) {
            Game.model.data.monsters.some((monster: IMonster) => {
                const reelCornerIdx: number = monster.symbol - Game.MONSTER_SYMBOLS.length;
                const monsterIdx: number = +monsterSymbol.name.charAt(1) - 1;
                if (reelCornerIdx === monsterIdx) {
                    lifeLeft = Game.reelCorners?.getLife(reelCornerIdx) ?? monster.lifeLeft;
                    return true;
                }
                return false;
            });
        }

        if (lifeLeft === 0){
            lifeLeft = Game.model.data.lifeLeftInitial;
        }

        if (lifeLeft === 1) {
            woundSkin = MONSTER_WEAPON_ACTIONS.HURT2;
        } else if (lifeLeft === 2) {
            woundSkin = MONSTER_WEAPON_ACTIONS.HURT1;
        }

        Game.MONSTERS_HURT_STATUS[Game.MONSTER_NAME_TO_IDX[monsterSymbol.name]] = (woundSkin !== "base");

        monsterSymbol.changeSymbolSkin(woundSkin);
    }

    private getMultiplierLandSoundKey(multiplier: string): string {
        const multiplierVal: number = +multiplier.substring(1);
        let soundKey: string = "";

        if (multiplierVal >= 10 && multiplierVal <= 50) {
            soundKey = "crateLandM10-M50";
        } else if (multiplierVal >= 100 && multiplierVal <= 200) {
            soundKey = "crateLandM100-M200";
        } else if (multiplierVal === 500) {
            soundKey = "crateLandM500";
        } else if (multiplierVal === 1000) {
            soundKey = "crateLandM1000";
        }

        return soundKey;
    }

    set mxStepInCollapse(value: number) {
        this._mxStepInCollapse = value;
    }

    set id(value: number) {
        this._id = value;
    }

    get numSymbolsRemovedForAvalanche(): number {
        return this._numSymbolsRemovedForAvalanche;
    }

    get symbols(): Symbol[] {
        return this._symbols;
    }
}